import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, errMsg, getAuthToken } from '../api/client';
import { useFocusTrap } from '../lib/useFocusTrap';
import { useVoiceRecorder } from '../lib/useVoiceRecorder';
import { Button, useToast } from '../ui';
import { useNotifications } from './NotificationProvider';
import {
  clock,
  timeAgoFa,
  toFaDigits,
  type Contact,
  type Message,
  type MsgRef,
  type Selection,
} from './types';

/** Friendly Persian label for a message's linked record (so the chip reads «قرارداد #۴۱۲», not «deal #412»). */
const REF_LABEL_FA: Record<string, string> = { deal: 'قرارداد', release: 'حواله', cheque: 'چک' };
const refLabelFa = (t: string) => REF_LABEL_FA[t] ?? t;

/**
 * The messaging drawer: a channel list (a shared broadcast channel + one thread per colleague) and
 * the active conversation with a text + voice composer. Voice notes are recorded with MediaRecorder
 * and posted as base64; they play back through the participant-scoped /api/messages/voice/:docId.
 * The active thread polls so replies land without a refresh. Opening a conversation clears its
 * notifications.
 */

const THREAD_POLL_MS = 8_000;

async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  const CHUNK = 0x8000; // chunked, not per-byte — a per-byte concat freezes on a multi-MB note
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/**
 * Plays a voice note. Fetches with the Authorization header (never the token in a URL, which would
 * leak into logs/history) from the participant-scoped route, into an object URL revoked on unmount.
 */
function VoiceNote({ docId }: { docId: number }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let url: string | null = null;
    let alive = true;
    void (async () => {
      const res = await fetch(`/api/messages/voice/${docId}`, {
        headers: { Authorization: `Bearer ${getAuthToken() ?? ''}` },
      });
      if (!res.ok || !alive) return;
      url = URL.createObjectURL(await res.blob());
      if (alive) setSrc(url);
      else URL.revokeObjectURL(url);
    })();
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [docId]);
  return src ? (
    <audio controls preload="none" src={src} className="msg-audio" />
  ) : (
    <span className="msg-audio muted">🎤 …</span>
  );
}

export function MessagingPanel({
  selection,
  composeRef,
  onClearRef,
  onClose,
}: {
  selection: Selection;
  composeRef?: MsgRef | null;
  onClearRef?: () => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const { items, markRead, reload: reloadNotifs } = useNotifications();
  const rec = useVoiceRecorder();

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [sel, setSel] = useState<Selection>(selection);
  const [messages, setMessages] = useState<Message[]>([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [listOpen, setListOpen] = useState(false); // narrow-screen sidebar toggle
  const logRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  // Modal drawer: trap focus, inert the app shell behind it, Esc closes, restore focus on close.
  useFocusTrap(drawerRef, true, { onEscape: onClose, backgroundSelector: '.applayout' });

  // Follow an external re-open (e.g. a second notification tap) onto its conversation.
  useEffect(() => setSel(selection), [selection]);

  useEffect(() => {
    void api<Contact[]>('/api/messages/contacts').then((r) => {
      if (r.ok) setContacts(r.body as Contact[]);
    });
  }, []);

  const target = useMemo(
    () =>
      sel.kind === 'broadcast' ? '/api/messages/broadcast' : `/api/messages/thread/${sel.userId}`,
    [sel],
  );

  const loadThread = useCallback(async () => {
    const r = await api<Message[]>(target);
    if (r.ok) setMessages(r.body as Message[]);
  }, [target]);

  // Load + poll the active conversation.
  useEffect(() => {
    void loadThread();
    const iv = window.setInterval(() => void loadThread(), THREAD_POLL_MS);
    const onFocus = () => void loadThread();
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(iv);
      window.removeEventListener('focus', onFocus);
    };
  }, [loadThread]);

  // Mark this conversation's notifications read — self-healing: fires on open AND whenever a new
  // notification for the open conversation lands while it's in view. Keyed on `items`, so it can't
  // miss the cold-open case where the notification list populates a tick after the panel mounts.
  useEffect(() => {
    const clearable = items
      .filter(
        (n) =>
          !n.read &&
          (sel.kind === 'broadcast'
            ? n.ref_type === 'broadcast'
            : n.ref_type === 'thread' && n.ref_id === String(sel.userId)),
      )
      .map((n) => n.id);
    if (clearable.length) void markRead(clearable);
  }, [items, sel, markRead]);

  // Keep the newest message in view (scroll the log itself, never the page).
  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const toUserId = sel.kind === 'broadcast' ? null : sel.userId;

  async function post(payload: Record<string, unknown>): Promise<boolean> {
    setSending(true);
    // Tag the message with the record it's about (TKT-5), so a hand-off links back to the deal/release.
    const ref = composeRef ? { refType: composeRef.type, refId: composeRef.id } : {};
    const r = await api<Message>('/api/messages', {
      method: 'POST',
      body: JSON.stringify({ toUserId, ...ref, ...payload }),
    });
    setSending(false);
    if (!r.ok) {
      toast(errMsg(r, 'خطا در ارسال پیام'), 'error');
      return false;
    }
    await loadThread();
    void reloadNotifs();
    return true;
  }

  async function sendText() {
    const body = text.trim();
    if (!body || sending) return;
    if (await post({ body })) setText('');
  }

  async function stopAndSendVoice() {
    const blob = await rec.stop();
    if (!blob) return;
    const dataBase64 = await blobToBase64(blob);
    await post({ voice: { dataBase64, mime: blob.type || 'audio/webm' } });
  }

  const title =
    sel.kind === 'broadcast'
      ? 'کانال عمومی'
      : (sel.name ?? contacts.find((c) => c.id === sel.userId)?.name ?? 'گفتگو');

  return (
    <div className="msg-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="msg-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="پیام‌ها"
        ref={drawerRef}
      >
        <header className="msg-head">
          <button
            type="button"
            className="msg-hamb"
            aria-label="گفتگوها"
            onClick={() => setListOpen((v) => !v)}
          >
            ☰
          </button>
          <b style={{ flex: 1 }}>{title}</b>
          <button type="button" className="msg-x" aria-label="بستن" onClick={onClose}>
            ✕
          </button>
        </header>

        <div className="msg-body">
          <nav className={listOpen ? 'msg-side msg-side-open' : 'msg-side'} aria-label="گفتگوها">
            <button
              type="button"
              className={sel.kind === 'broadcast' ? 'msg-contact active' : 'msg-contact'}
              onClick={() => {
                setSel({ kind: 'broadcast' });
                setListOpen(false);
              }}
            >
              📢 کانال عمومی
            </button>
            {contacts.map((c) => {
              const active = sel.kind === 'user' && sel.userId === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  className={active ? 'msg-contact active' : 'msg-contact'}
                  onClick={() => {
                    setSel({ kind: 'user', userId: c.id, name: c.name });
                    setListOpen(false);
                  }}
                >
                  <span className="msg-avatar" aria-hidden>
                    {(c.name || c.username).slice(0, 1)}
                  </span>
                  <span className="msg-contact-name">{c.name || c.username}</span>
                </button>
              );
            })}
          </nav>

          <section className="msg-thread">
            <div className="msg-log" ref={logRef}>
              {messages.length === 0 ? (
                <p className="muted" style={{ textAlign: 'center', marginTop: 40 }}>
                  هنوز پیامی نیست — گفتگو را شروع کنید.
                </p>
              ) : (
                messages.map((m) => (
                  <div key={m.id} className={m.mine ? 'msg-row mine' : 'msg-row'}>
                    <div className={m.mine ? 'msg-bubble mine' : 'msg-bubble'}>
                      {sel.kind === 'broadcast' && !m.mine ? (
                        <div className="msg-sender">{m.from_name || m.from_username}</div>
                      ) : null}
                      {m.body ? <div className="msg-text">{m.body}</div> : null}
                      {m.voice_doc_id ? <VoiceNote docId={m.voice_doc_id} /> : null}
                      {m.ref_type && m.ref_id ? (
                        <div className="msg-ref">
                          ↪ {refLabelFa(m.ref_type)} #{toFaDigits(m.ref_id)}
                        </div>
                      ) : null}
                      <div className="msg-meta">{timeAgoFa(m.created_at)}</div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {composeRef ? (
              <div
                style={{
                  margin: '0 8px 6px',
                  padding: '6px 10px',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--field-focus)',
                  border: '1px solid var(--line)',
                  color: 'var(--navy)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: '.85em',
                }}
              >
                <span>
                  دربارهٔ <b>{composeRef.label}</b>
                </span>
                <span style={{ flex: 1 }} />
                {onClearRef ? (
                  <button
                    type="button"
                    className="msg-x"
                    aria-label="حذف پیوند"
                    onClick={onClearRef}
                  >
                    ✕
                  </button>
                ) : null}
              </div>
            ) : null}
            <div className="msg-composer">
              {rec.recording ? (
                <div className="msg-recording">
                  <span className="rec-dot" aria-hidden />
                  <span>در حال ضبط… {clock(rec.seconds)}</span>
                  <span style={{ flex: 1 }} />
                  <button
                    type="button"
                    className="msg-icon danger"
                    aria-label="لغو ضبط"
                    onClick={rec.cancel}
                  >
                    ✕
                  </button>
                  <Button onClick={() => void stopAndSendVoice()} loading={sending}>
                    ارسال صوت
                  </Button>
                </div>
              ) : (
                <>
                  <input
                    className="input"
                    style={{ flex: 1 }}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        void sendText();
                      }
                    }}
                    placeholder="پیام بنویسید…"
                    disabled={sending}
                  />
                  {rec.supported ? (
                    <button
                      type="button"
                      className="msg-icon"
                      aria-label="ضبط پیام صوتی"
                      title="پیام صوتی"
                      onClick={() => void rec.start()}
                      disabled={sending}
                    >
                      🎤
                    </button>
                  ) : null}
                  <Button onClick={() => void sendText()} loading={sending} disabled={!text.trim()}>
                    ارسال
                  </Button>
                </>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
